import { IkeCollectorType, IkeToken } from '@ikegps/core-api';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { addStyles } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { m } from '../../../mithril';
import { FormDesignerPalette } from '../form.palette';
import type { FormFieldEditor } from './form.field.editor';
import type { FormFieldEditorSubForm } from './subform/form.field.editor.subform';
import { CSS, Style } from './form.field.tree.view.style';
import type { FormFieldEditorForm } from './form.field.editor.form';
import { Services } from '../../../services';

addStyles(Style);

export class FormFieldTreeView {
	static view(editor: FormFieldEditor) {
		const isSelected = editor.designer.selectedFieldId === editor.field.fieldId;
		const className = `${CSS.container} ${isSelected ? CSS.isSelected : ''}`;

		return m('div', { className }, FormFieldTreeView.viewButton(editor));
	}

	static viewForm(editor: FormFieldEditorForm | FormFieldEditorSubForm): MithrilVNode {
		const children: MithrilVNode[] = [];
		for (const field of editor.field.fields) {
			children.push(editor.designer.viewField(field));
		}

		children.push(FormFieldTreeView.viewButtonNew(editor));

		return m('div', { className: CSS.form.container }, [
			FormFieldTreeView.view(editor),
			m('div', { className: CSS.form.children }, children),
		]);
	}

	private static viewButton(editor: FormFieldEditor): MithrilVNode {
		const isCloneable = editor.field.isCloneable;
		const isTitle = editor.field.title;
		const isLocked = editor.field.isLocked;
		const isSubtitle = editor.field.subtitle && Services.Token.has(IkeToken.FEATURE_SECONDARY_LABEL);
		const isHidden = editor.field.collector === IkeCollectorType.OFFICE;
		const isAdminOnly = editor.field.adminOnly && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		const isStatus = editor.field.isStatusField && Services.Token.has(IkeToken.FEATURE_SORT_STATUS);
		const isRequired = editor.field.required && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		const isMapColor = editor.field.isMapColorField && Services.Token.has(IkeToken.FEATURE_COLOR_FLAG);
		const isAddress = editor.field.isAddressLookupField && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		let tags: MithrilVNode = null;

		if (
			isCloneable ||
			isTitle ||
			isLocked ||
			isSubtitle ||
			isHidden ||
			isAdminOnly ||
			isRequired ||
			isStatus ||
			isMapColor ||
			isAddress
		) {
			tags = m('div', { className: CSS.tag.list }, [
				isTitle
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_LABEL),
							},
							'L'
						)
					: null,
				isSubtitle
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_SECONDARY_LABEL),
							},
							'SL'
						)
					: null,
				isCloneable
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.cloneable,
								title: Translate(T.FORM_OPTION_CLONEABLE),
							},
							'C'
						)
					: null,
				isHidden
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_HIDE),
							},
							'H'
						)
					: null,
				isAdminOnly
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_ADMIN_ONLY),
							},
							'A'
						)
					: null,
				isStatus
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.status,
								title: Translate(T.FORM_OPTION_STATUS),
							},
							'ST'
						)
					: null,
				isMapColor
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.status,
								title: Translate(T.FORM_OPTION_MAP_COLOR),
							},
							'M'
						)
					: null,
				isLocked
					? Icon.view(MaterialIcon.LOCK, {
							className: CSS.tag.tag + ' ' + CSS.tag.cloneable,
							title: Translate(T.FORM_OPTION_LOCKED),
						})
					: null,
				isRequired
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_REQUIRED),
							},
							'R'
						)
					: null,
				isAddress
					? m(
							'div',
							{
								className: CSS.tag.tag + ' ' + CSS.tag.label,
								title: Translate(T.FORM_OPTION_ADDRESS),
							},
							'AD'
						)
					: null,
			]);
		}

		let text = editor.field.displayName;
		if (text == null || text.trim().length === 0) {
			text = Translate(T.FIELD_DISPLAY_NAME_MISSING);
		}

		const suffix = editor.treeLabelSuffix;
		if (suffix != null) {
			text = `${text} ${suffix}`;
		}

		return m(
			'button',
			{
				className: `mdl-button ${CSS.title} ${editor.state.isValid ? '' : CSS.isInvalid}`,
				onclick: () => editor.designer.setSelected(editor.field),
			},
			[
				m('div', { className: CSS.label.container }, [
					FormDesignerPalette.viewIcon(editor.field.type, { className: CSS.label.icon }),
					m('div', { className: CSS.label.text, title: text }, text),
				]),
				tags,
			]
		);
	}

	private static viewButtonNew(editor: FormFieldEditor): MithrilVNode {
		const title = Translate(T.FORM_FIELD_ADD, { name: editor.field.displayName });
		return m('button', { className: `mdl-button ${CSS.title}`, onclick: editor.addField }, [
			m('div', { className: CSS.label.container }, [
				Icon.view(MaterialIcon.ADD, { className: CSS.label.icon }),
				m('div', { className: CSS.label.text, title }, title),
			]),
		]);
	}
}
